import React, { useEffect, useRef, useState } from 'react';
import { loadGoogleMapsScript } from '../../utils/googleMapsLoader';
import type { MapRouteOrigin } from '../BranchMapPickerModal';
import Icon from '../Icon';
import { FiLoader, FiMapPin } from 'react-icons/fi';

type Props = {
    apiKey: string | undefined;
    routeOrigin: MapRouteOrigin | null;
    latitude: string;
    longitude: string;
    /** Region label for messaging when anchor A (region lat/long) is missing. */
    locationName?: string;
    locationId?: string;
};

function parseCoord(s: string): number {
    const n = parseFloat(String(s).replace(/,/g, '').trim());
    return Number.isNaN(n) ? NaN : n;
}

function coordsValid(lat: number, lng: number): boolean {
    return Number.isFinite(lat) && Number.isFinite(lng);
}

/** Frosted panel (message card). */
const glassCardClass =
    'pointer-events-none max-w-md rounded-2xl border border-white/60 bg-white/55 px-6 py-5 text-center shadow-[0_8px_40px_rgba(0,0,0,0.12)] backdrop-blur-xl dark:border-white/15 dark:bg-gray-950/50 dark:shadow-black/50';

/** Full-area glass veil over map when B is missing. */
const glassVeilClass =
    'pointer-events-none absolute inset-0 flex items-center justify-center bg-slate-900/25 p-4 backdrop-blur-[3px] dark:bg-black/35';

/** Standalone placeholder with glass frame (no map). */
const glassFrameClass =
    'flex h-[min(22rem,50vh)] min-h-[200px] w-full flex-col items-center justify-center gap-4 rounded-xl border border-white/40 bg-gradient-to-br from-white/50 via-gray-100/40 to-gray-200/50 px-6 text-center shadow-inner backdrop-blur-md dark:border-white/10 dark:from-gray-900/50 dark:via-gray-900/40 dark:to-black/40';

/**
 * Read-only map: driving route from region (A) to branch (B). Always shows a map-sized area;
 * missing API key or coordinates shows guidance text instead of hiding the section.
 */
export default function BranchRoutePreviewMap({
    apiKey,
    routeOrigin,
    latitude,
    longitude,
    locationName = '',
    locationId = '',
}: Props) {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<google.maps.Map | null>(null);
    const markersRef = useRef<google.maps.Marker[]>([]);
    const directionsRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
    const directionsServiceRef = useRef<google.maps.DirectionsService | null>(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [routeSummary, setRouteSummary] = useState<string | null>(null);

    const lat = parseCoord(latitude);
    const lng = parseCoord(longitude);
    const branchValid = coordsValid(lat, lng);
    const hasOrigin =
        routeOrigin != null &&
        Number.isFinite(routeOrigin.lat) &&
        Number.isFinite(routeOrigin.lng);

    const originKey = hasOrigin ? `${routeOrigin!.lat.toFixed(6)},${routeOrigin!.lng.toFixed(6)}` : '';
    const branchKey = branchValid ? `${lat.toFixed(6)},${lng.toFixed(6)}` : '';
    const keyTrim = apiKey?.trim() ?? '';
    const hasApiKey = Boolean(keyTrim);

    const nameTrim = locationName.trim();
    const nameOk = Boolean(nameTrim && nameTrim !== 'N/A');
    const regionDisplayName = nameOk ? nameTrim : 'this region';
    const expectsRegionAnchor =
        Boolean(locationId && locationId !== 'custom') || nameOk;

    useEffect(() => {
        if (!hasApiKey || !containerRef.current) {
            setLoading(false);
            setError(null);
            setRouteSummary(null);
            return;
        }

        let cancelled = false;
        setError(null);
        setRouteSummary(null);

        // No branch coords: optional map centered on region only
        if (!branchValid) {
            if (!hasOrigin || !routeOrigin) {
                setLoading(false);
                return;
            }

            setLoading(true);
            void (async () => {
                try {
                    await loadGoogleMapsScript(keyTrim);
                    if (cancelled || !containerRef.current) return;

                    const map = new google.maps.Map(containerRef.current, {
                        center: { lat: routeOrigin.lat, lng: routeOrigin.lng },
                        zoom: 11,
                        mapTypeControl: true,
                        streetViewControl: false,
                        fullscreenControl: true,
                    });
                    mapRef.current = map;
                    const regionMarker = new google.maps.Marker({
                        position: { lat: routeOrigin.lat, lng: routeOrigin.lng },
                        map,
                        title: 'Region',
                        label: { text: 'A', color: 'white', fontWeight: 'bold' },
                        icon: {
                            path: google.maps.SymbolPath.CIRCLE,
                            scale: 10,
                            fillColor: '#16a34a',
                            fillOpacity: 1,
                            strokeColor: '#ffffff',
                            strokeWeight: 2,
                        },
                    });
                    markersRef.current = [regionMarker];
                } catch (e: unknown) {
                    if (!cancelled) {
                        setError(e instanceof Error ? e.message : 'Could not load map');
                    }
                } finally {
                    if (!cancelled) setLoading(false);
                }
            })();

            return () => {
                cancelled = true;
                markersRef.current.forEach((m) => m.setMap(null));
                markersRef.current = [];
                mapRef.current = null;
                if (containerRef.current) containerRef.current.innerHTML = '';
            };
        }

        // Full route or branch-only map
        setLoading(true);
        void (async () => {
            try {
                await loadGoogleMapsScript(keyTrim);
                if (cancelled || !containerRef.current) return;

                const map = new google.maps.Map(containerRef.current, {
                    center: { lat, lng },
                    zoom: 14,
                    mapTypeControl: true,
                    streetViewControl: false,
                    fullscreenControl: true,
                });
                mapRef.current = map;
                directionsServiceRef.current = new google.maps.DirectionsService();
                const dr = new google.maps.DirectionsRenderer({
                    map,
                    suppressMarkers: true,
                    preserveViewport: true,
                    polylineOptions: {
                        strokeColor: '#2563eb',
                        strokeOpacity: 0.92,
                        strokeWeight: 5,
                    },
                });
                directionsRendererRef.current = dr;

                if (hasOrigin && routeOrigin) {
                    const originM = new google.maps.Marker({
                        position: { lat: routeOrigin.lat, lng: routeOrigin.lng },
                        map,
                        title: 'Region',
                        label: { text: 'A', color: 'white', fontWeight: 'bold' },
                        icon: {
                            path: google.maps.SymbolPath.CIRCLE,
                            scale: 10,
                            fillColor: '#16a34a',
                            fillOpacity: 1,
                            strokeColor: '#ffffff',
                            strokeWeight: 2,
                        },
                    });
                    const destM = new google.maps.Marker({
                        position: { lat, lng },
                        map,
                        title: 'Branch',
                        label: { text: 'B', color: 'white', fontWeight: 'bold' },
                        icon: {
                            path: google.maps.SymbolPath.CIRCLE,
                            scale: 10,
                            fillColor: '#dc2626',
                            fillOpacity: 1,
                            strokeColor: '#ffffff',
                            strokeWeight: 2,
                        },
                    });
                    markersRef.current = [originM, destM];

                    const bounds = new google.maps.LatLngBounds();
                    bounds.extend({ lat: routeOrigin.lat, lng: routeOrigin.lng });
                    bounds.extend({ lat, lng });
                    map.fitBounds(bounds, { top: 48, right: 48, bottom: 48, left: 48 });

                    directionsServiceRef.current.route(
                        {
                            origin: { lat: routeOrigin.lat, lng: routeOrigin.lng },
                            destination: { lat, lng },
                            travelMode: google.maps.TravelMode.DRIVING,
                        },
                        (result, status) => {
                            if (cancelled) return;
                            if (status === google.maps.DirectionsStatus.OK && result?.routes?.[0]?.legs?.[0]) {
                                dr.setDirections(result);
                                const leg = result.routes[0].legs[0];
                                const parts = [leg.distance?.text, leg.duration?.text].filter(Boolean);
                                setRouteSummary(parts.join(' · '));
                            } else {
                                try {
                                    dr.setDirections({ routes: [] } as any);
                                } catch {
                                    /* ignore */
                                }
                                setRouteSummary(null);
                            }
                        }
                    );
                } else {
                    const m = new google.maps.Marker({
                        position: { lat, lng },
                        map,
                        title: 'Branch',
                    });
                    markersRef.current = [m];
                    map.setCenter({ lat, lng });
                    map.setZoom(15);
                }
            } catch (e: unknown) {
                if (!cancelled) {
                    setError(e instanceof Error ? e.message : 'Could not load map');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            try {
                directionsRendererRef.current?.setMap(null);
            } catch {
                /* ignore */
            }
            directionsRendererRef.current = null;
            directionsServiceRef.current = null;
            markersRef.current.forEach((m) => m.setMap(null));
            markersRef.current = [];
            mapRef.current = null;
            if (containerRef.current) containerRef.current.innerHTML = '';
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasApiKey, keyTrim, branchKey, originKey, hasOrigin, branchValid, lat, lng]);

    const title =
        hasOrigin && branchValid
            ? 'Map: region → branch (driving route)'
            : hasOrigin && !branchValid
              ? 'Map: region anchor (branch point B missing)'
              : branchValid && !hasOrigin
                ? 'Map: branch location (region anchor A missing)'
                : 'Map: region & branch';

    const adminRegionMessage = (
        <div className={glassCardClass}>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Region anchor (A) is not available
            </p>
            <p className="mt-2 text-xs leading-relaxed text-gray-700 dark:text-gray-300">
                Ask an admin to add <strong>longitude and latitude</strong> for{' '}
                <strong className="text-gray-900 dark:text-white">{regionDisplayName}</strong> in{' '}
                <strong>Locations</strong> so the map can show point A and the route to this branch (B).
            </p>
        </div>
    );

    const branchBMissingMessage = (
        <div className={glassCardClass}>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Branch point (B) is not set</p>
            <p className="mt-2 text-xs leading-relaxed text-gray-700 dark:text-gray-300">
                Update <strong>longitude and latitude</strong> for this branch (or pick on the map when editing). The
                frosted overlay will clear once point B is saved.
            </p>
        </div>
    );

    return (
        <div className="mt-4 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="form-field-label mb-0">{title}</span>
                {routeSummary && branchValid && hasOrigin ? (
                    <span className="text-xs text-gray-600 dark:text-gray-400">{routeSummary}</span>
                ) : null}
            </div>

            {!hasApiKey ? (
                <div className={glassFrameClass}>
                    <Icon icon={FiMapPin} className="h-10 w-10 text-gray-400" />
                    <div className={`${glassCardClass} pointer-events-auto`}>
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                            Google Maps API key is not configured
                        </p>
                        <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                            Add{' '}
                            <code className="rounded bg-black/5 px-1 py-0.5 dark:bg-white/10">
                                REACT_APP_GOOGLE_MAPS_API_KEY
                            </code>{' '}
                            to your environment to show the map.
                        </p>
                    </div>
                </div>
            ) : !branchValid && !hasOrigin ? (
                <div className={glassFrameClass}>
                    <Icon icon={FiMapPin} className="h-10 w-10 text-amber-500/90" />
                    {expectsRegionAnchor ? (
                        adminRegionMessage
                    ) : (
                        <div className={glassCardClass}>
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                Set region and branch coordinates
                            </p>
                            <p className="mt-2 text-xs leading-relaxed text-gray-700 dark:text-gray-300">
                                Choose a <strong>region</strong> and ask an admin to add its map coordinates in{' '}
                                <strong>Locations</strong> if needed, then add <strong>longitude and latitude</strong>{' '}
                                for this branch so points A and B can appear on the map.
                            </p>
                        </div>
                    )}
                </div>
            ) : (
                <div className="relative overflow-hidden rounded-xl border border-gray-200/80 bg-gray-100 dark:border-gray-600 dark:bg-gray-900/40">
                    <div ref={containerRef} className="h-[min(22rem,50vh)] w-full min-h-[200px]" />
                    {!branchValid && hasOrigin ? <div className={glassVeilClass}>{branchBMissingMessage}</div> : null}
                    {branchValid && !hasOrigin ? <div className={glassVeilClass}>{adminRegionMessage}</div> : null}
                    {loading && (
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-sm dark:bg-gray-900/50">
                            <Icon icon={FiLoader} className="h-8 w-8 animate-spin text-gray-500" />
                        </div>
                    )}
                    {error && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/90 p-4 text-center text-sm text-red-600 backdrop-blur-sm dark:bg-gray-900/90 dark:text-red-400">
                            {error}
                        </div>
                    )}
                </div>
            )}

            {hasOrigin && branchValid ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    A = selected region anchor · B = branch coordinates
                </p>
            ) : null}
        </div>
    );
}
